import type { TQGridColumn } from 'components/TQDataGrid/utils/grid-types';
import type { InputCiqGridColDef } from 'muigrid';
import type { CiqGridColConditionalFormattingRule } from 'muigrid/types/conditionalFormatting';
import type { CollaboratorRoleAccessType, SourceType, StringifiedUUID, User } from 'types';
import type { DataField } from 'types/dataFields';

import type { TQModel } from '../tqModels';

export enum SPMWorksheetType {
  INPUT = 'input',
  METRICS = 'metrics',
  TIME_BASED = 'time_based',
  ROSTER = 'roster',
  ROSTER_BASED = 'roster_based',
  QUOTA = 'quota',
  PROPERTY = 'property',
  PROPERTY_BASED = 'property_based',
  ACCOUNTS = 'accounts',
  TERRITORIES = 'territories',
}

export const TQ_WORKSHEET_TYPES = new Set<SPMWorksheetType>([SPMWorksheetType.ACCOUNTS, SPMWorksheetType.TERRITORIES]);

export const isTqDataObjectSchemaWorksheetType = (worksheetType: string): boolean =>
  TQ_WORKSHEET_TYPES.has(worksheetType as SPMWorksheetType);

export type SPMPreviewWorksheet = {
  id: string;
  name: string;
  type?: SPMWorksheetType;
  columns: Array<{ id: string; name: string; data_type?: string; variant?: string; source?: string }>;
};

export enum CapacitySystemTabs {
  ROSTER = 'Roster',
}

export enum RampCalculationCadence {
  MONTHS = 'months',
  DAYS = 'days',
}

export enum RampCalculationColumnType {
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
}

export enum SPMWorksheetLayerType {
  PLANNING_CYCLE = 'planning_cycle',
  SCENARIO = 'scenario',
  MODEL = 'model',
}

export type SPMModelRequest = {
  id?: StringifiedUUID;
  name: string;
  model_type?: 'quota' | 'capacity';
  starter_template?: string;
  planning_cycle_id?: string;
};

export type SPMColumnVariant =
  | 'raw'
  | 'derived'
  | 'picklist'
  | 'unique_id'
  | 'compound_id'
  | 'transformed'
  | 'roster'
  | 'property'
  | 'system';

// Unified request type for creating columns (matches SPMColumnCreateSerializer)
export type SPMColumnCreateRequest = {
  name: string;
  variant: SPMColumnVariant;
  data_type?: string;
  format?: Record<string, any>;
  formula?: string;
  property_source_id?: string;
  source?: string;
  description?: string;
  time_period?: string; // ISO date string
  order?: number;
  apply_to_time_columns?: boolean;
};

// Unified interface types that match the Python SPMWorksheetInterfaceSerializer
export type SPMColumnInterface = {
  id: string;
  name: string;
  data_type: string;
  variant: SPMColumnVariant;
};

export type SPMWorksheetInterface = {
  id: string;
  name: string;
  type: string;
  columns: SPMColumnInterface[];
};

export type PlanningCycleDate = {
  id: StringifiedUUID;
  planning_date: string;
  created_at: string;
  updated_at: string;
};

/**
 * Indicates that a column should be displayed differently than it's underlying type indicates.
 * e.g. a derived column with data_type String may be more accurately displayed as a read-only property column.
 */
type SPMColumnDisplayType = {
  data_type?: string;
  variant?: SPMColumnVariant;
};

// Original full SPM types
export type SPMColumn = {
  object: 'column';
  id: string;
  name: string;
  description: string;
  variant: SPMColumnVariant;
  data_type: string;
  format: Record<string, any>; // TODO: dedupe this from SPMDataGrid types
  created_at: string;
  updated_at: string;
  formula?: string;
  property_source_id?: string;
  upstream_column_uuid?: string;
  default_value?: string;
  source?: string;
  is_time_period_column?: boolean;
  is_decomposed_column?: boolean;
  time_period?: PlanningCycleDate;
  display_as?: SPMColumnDisplayType;
  conditional_formatting?: CiqGridColConditionalFormattingRule[];
  system_column_type?: string | null;
};

export type SPMColumnGroup = {
  object: 'column_group';
  id: string;
  name: string;
  description: string;
  order: number;
  column_order: string[];
  created_at: string;
  updated_at: string;
};

export type SPMPropertyValue = {
  object: 'property_value';
  id: string;
  label: string;
};

export type SPMWorksheetVersionedCopyDefinition = {
  id: string;
  source_type: SourceType;
  source_id: string;
  column_mapping: Record<string, any>;
  sync_mode: string;
  created_at: string;
  updated_at: string;

  last_task_uuid?: string;
  last_sync_time?: string;
  last_sync_status?: string | null;
};

export type SPMWorksheet = {
  object: 'worksheet';
  id: string;
  name: string;
  description: string;
  is_system: boolean;
  is_output?: boolean;
  planning_cycle_id?: string;
  type?: SPMWorksheetType;
  layer?: SPMWorksheetLayerType;
  tab?: string;
  columns: SPMColumn[];
  column_groups: SPMColumnGroup[];
  created_at: string;
  updated_at: string;
  has_time_columns?: boolean;
  time_period_column_spec?: {
    variant: string;
    data_type: string;
    format: {
      type: string;
      currency_code?: string;
      places?: string;
    };
  };
  columns_width?: Record<StringifiedUUID, number>;
  versioned_copy_definition?: SPMWorksheetVersionedCopyDefinition | null;
  property_worksheet_ids?: string[];
};

export type SPMAffectedWorksheetScenario = {
  id: StringifiedUUID;
  name: string;
  records: StringifiedUUID[];
};

export type SPMAffectedWorksheet = {
  id: StringifiedUUID;
  planning_cycle_id: StringifiedUUID;
  spm_model_id: StringifiedUUID | undefined;
  spm_model_type: SPMModelType | undefined;
  name: string;
  tab: string | undefined;
  scenarios: SPMAffectedWorksheetScenario[];
};

export enum SPMModelType {
  CAPACITY = 'capacity',
  QUOTA = 'quota',
}

export enum SPMWorksheetInputDataVersioningMode {
  COPY = 'copy',
  VIEW = 'view',
}

export enum SPMWorksheetInputDataSyncMode {
  OVERWRITE = 'overwrite',
  UPSERT = 'upsert',
}

export type SPMModel = {
  object: 'spm_model';
  id: string;
  organization_id: string;
  planning_cycle_id?: string;
  name: string;
  model_type: SPMModelType;
  worksheets?: SPMWorksheet[];
  created_at: string;
  updated_at: string;
};

// Type for column values for a worksheet
export type SPMRecord = {
  object: string;
  id: string;
  data: Record<string, string | null>;
};

export type SPMRecordsResponse = {
  object: string;
  data: SPMRecord[];
};

export type SPMWorksheetRosterMember = {
  object: 'employee' | 'tbh';
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
};

export type SPMScenario = {
  object: 'spm_scenario';
  id: StringifiedUUID;
  planning_cycle_id: StringifiedUUID;
  name: string;
  description: string;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  hierarchy_model: TQModel;
};

export type AccountsDataForm = {
  sourceType: SourceType;
  worksheet: string;
  uniqueColumn: string;
  parentColumn?: string;
  nameColumn?: string;
  autoUpdate?: boolean;
};

export type TabWorksheetOrder = {
  worksheet_id: StringifiedUUID;
  worksheet_name: string;
  order: number;
};

export type SPMModelTab = {
  object: 'spm_model_tab';
  id: string;
  name: string;
  is_system: boolean;
  order: number;
  worksheet_order: TabWorksheetOrder[];
};

export type SPMModelTabRequest = {
  name: string;
  order?: number;
  worksheet_order?: Pick<TabWorksheetOrder, 'worksheet_id' | 'order'>[];
};

export type SPMMetricsPage = {
  id: StringifiedUUID;
  title: string;
  metrics_group_uuid: StringifiedUUID;
};

export type SPMCollaborationFilter = {
  filter_group_id: StringifiedUUID;
  filter_view_id?: StringifiedUUID;
};

export type SPMCollaborator = {
  id: StringifiedUUID;
  created_at: string;
  user: Pick<User, 'id' | 'email' | 'first_name' | 'last_name'>;
  role:
    | CollaboratorRoleAccessType.COLLABORATOR
    | CollaboratorRoleAccessType.COMMENTER
    | CollaboratorRoleAccessType.APPROVER;
  property_filter?: SPMCollaborationFilter;
  column_filter?: SPMCollaborationFilter;
};

export type MetricColumn = SPMColumn | DataField;

export type MetricDataGridColumn = InputCiqGridColDef | TQGridColumn;

export interface TabModalData {
  mode: 'add' | 'edit';
  modelType: 'capacity' | 'quota';
  modelId: StringifiedUUID;
  tabId?: StringifiedUUID; // Only for edit mode
  initialTabName?: string; // Only for edit mode
}

export type QuotaPolicyType = 'base_quota' | 'seasonality' | 'ramp' | 'exceptions';
export type QuotaPolicyTabType = QuotaPolicyType | 'custom';

export type RampPolicyConfiguration = {
  ramp_calculation_cadence: RampCalculationCadence;
  tenure_start_date_column_uuid: string;
  ramp_calculation_attribute_column_uuid?: string;
  ramp_calculation_attribute_values?: string[]; // Array of strings (backend format)
  ramp_calculation_column_type: RampCalculationColumnType;
  ramp_phases_thresholds: number[];
};

export type BaseQuotaPolicyConfiguration = {
  base_quota_attribute_column_uuid: StringifiedUUID;
  base_quota_attribute_values: string[];
};

export type QuotaPolicyConfiguration = {
  ramp?: RampPolicyConfiguration;
  base_quota?: BaseQuotaPolicyConfiguration;
  seasonality?: any; // TODO: Define seasonality configuration type
  exceptions?: any; // TODO: Define exceptions configuration type
};

export const TerritoriesModelViewQueryParam = 'view';

export enum TerritoriesModelView {
  UNIFIED = 'unified',
  ANALYTICS = 'analytics',
  GRID = 'grid',
}

export const NAVIGATION_ANIMATION_DURATION = 500; // ms

// SPM Connectors types
export type SPMConnectorRoutingInfo = {
  object: 'spm_connector';
  source_worksheet_uuid: StringifiedUUID;
  source_worksheet_name: string;
  source_scenario_uuid?: StringifiedUUID;
  source_scenario_name?: string;
  destination_worksheet_uuid: StringifiedUUID;
  destination_worksheet_name: string;
  destination_workbook_uuid: StringifiedUUID;
  destination_workbook_name: string;
};

// This will be set to null if the user is not a collaboration user
export type SPMCollaborationRole =
  | CollaboratorRoleAccessType.COLLABORATOR
  | CollaboratorRoleAccessType.COMMENTER
  | CollaboratorRoleAccessType.APPROVER
  | null;

// Utility type for collaboration metadata to pass around
export type CollaborationMetadata = {
  isCollaborationUser: boolean;
  collaborationRole: SPMCollaborationRole;
  isApprovalMode?: boolean;
};

export type SPMAffectedSavedFilter = {
  id: StringifiedUUID;
  planning_cycle_id: StringifiedUUID;
  spm_model_id: StringifiedUUID | undefined;
  spm_model_type: SPMModelType | undefined;
  name: string;
  collaborators_count: number;
};

export type SPMApprovalGroup = {
  id: StringifiedUUID;
  created_at: string;
  created_by_id: StringifiedUUID | null;
  created_by_name: string | null;
  approval_rules: 'all' | 'any';
  approval_type: 'single' | 'workflow';
  is_approved: boolean | null;
  completed_at: string | null;
  object_id: StringifiedUUID;
  object_type: 'spm_proposed_change_group';
  proposed_change_group_status: ProposedChangeGroupStatus | null;
  workflow_id: StringifiedUUID | null;
};

export type SPMApproval = {
  id: StringifiedUUID;
  created_at: string;
  approver_id: StringifiedUUID | null;
  approver_name: string | null;
  is_approved: boolean | null;
  resolved_at: string | null;
  message: string | null;
  approval_group: SPMApprovalGroup;
  scenario_id: StringifiedUUID | null;
  scenario_name: string | null;
  planning_cycle_id: StringifiedUUID | null;
  planning_cycle_name: string | null;
};

// Approval activity feed types — detailed shapes from ApprovalGroupsSerializer
export type ApprovalOverrideDetail = {
  id: StringifiedUUID;
  user_id: StringifiedUUID;
  user_name: string;
  is_approved: boolean;
  overriden_at: string;
  message: string;
};

export type ApprovalDetail = {
  id: StringifiedUUID;
  created_at: string;
  approver_id: StringifiedUUID;
  approver_name: string;
  approval_group_id: StringifiedUUID;
  approval_group_is_approved: boolean | null;
  approval_override: ApprovalOverrideDetail | null;
  is_approved: boolean | null;
  resolved_at: string | null;
  message: string | null;
};

export type ApprovalGroupDetail = {
  id: StringifiedUUID;
  approval_set: ApprovalDetail[];
  created_by: StringifiedUUID | null;
  created_by_name: string | null;
  created_at: string;
  approval_rules: 'all' | 'any';
  approval_type: 'single' | 'workflow';
  is_approved: boolean | null;
  completed_at: string | null;
  deleted: boolean;
  object_id: StringifiedUUID;
  object_type: string;
};

export enum ProposedChangeGroupStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

export enum ApprovalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
}

export type CollaboratorRole = 'edit' | 'commenter' | 'approver';

export type SPMWorkflowSummary = {
  id: StringifiedUUID;
  planning_cycle_id: StringifiedUUID | null;
  planning_cycle_name: string | null;
  scenario_id: StringifiedUUID | null;
  scenario_name: string | null;
  status: ApprovalStatus;
  created_at: string | null;
  active_approval_group: ApprovalGroupDetail | null;
};

// Workflow types for SPM planning cycle workflows
export type WorkflowType = 'data_sync' | 'approval';

export type PlanningCycleWorkflow = {
  planning_cycle_id: StringifiedUUID;
  workflow_id: StringifiedUUID;
  workflow_name: string;
  workflow_type: WorkflowType | null;
  scenario_id: StringifiedUUID | null;
  scenario_name: string | null;
  created_at: string;
  updated_at: string;
};
